import React from 'react';
import { Box, Container, Typography, Paper, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const SettingsContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(8),
  paddingBottom: theme.spacing(4),
}));

const SettingsPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  background: 'linear-gradient(180deg, rgba(255,255,255,0.85), rgba(255,255,255,0.92))',
  backdropFilter: 'blur(8px)',
  borderRadius: 'var(--radius-md)',
  boxShadow: 'var(--elevation-2)',
}));

const Settings = () => {
  const { logout, user } = useAuth();
  const [undoToken, setUndoToken] = React.useState(null);
  const [deletionRequested, setDeletionRequested] = React.useState(false);
  const [permanentDeleteLoading, setPermanentDeleteLoading] = React.useState(false);
  const [cancelLoading, setCancelLoading] = React.useState(false);
  const [cancelSuccess, setCancelSuccess] = React.useState(false);

  const handleDeleteAccount = async () => {
    if (!window.confirm('Are you sure you want to delete your account? This action cannot be undone. You will have 30 days to undo this action.')) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.delete('/api/users/me', {
        baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setUndoToken(res.data.undoToken);
      setDeletionRequested(true);
      alert('Your account is scheduled for deletion. You have 30 days to undo this action.');
      logout();
    } catch (error) {
      alert('Failed to delete account. Please try again.');
    }
  };

  const getDaysLeft = () => {
    if (!user?.deletionRequestedAt) return null;
    const requested = new Date(user.deletionRequestedAt);
    const now = new Date();
    const diff = 30 - Math.floor((now - requested) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  };

  const handlePermanentDelete = async () => {
    if (!window.confirm('This will permanently delete your account. You will not be able to recover it. Continue?')) return;
    setPermanentDeleteLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete('/api/users/permanent', {
        baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      alert('Your account has been permanently deleted.');
      logout();
    } catch (error) {
      alert('Failed to permanently delete account. Please try again.');
    } finally {
      setPermanentDeleteLoading(false);
    }
  };

  const handleCancelDeletion = async () => {
    setCancelLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/users/cancel-delete', {}, {
        baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setCancelSuccess(true);
      window.location.reload(); // To refetch user data and update UI
    } catch (error) {
      alert('Failed to cancel deletion. Please try again.');
    } finally {
      setCancelLoading(false);
    }
  };

  return (
    <SettingsContainer maxWidth="md">
      <Typography variant="h4" component="h1" gutterBottom sx={{ background: 'linear-gradient(90deg, var(--gradient-start), var(--gradient-end))', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', fontWeight: 'bold' }}>
        Settings
      </Typography>
      
      <SettingsPaper>
        <Typography variant="h6" gutterBottom>
          Account Settings
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your account preferences and security settings here.
        </Typography>
        <Box sx={{ mt: 2 }}>
          {user?.deletionRequestedAt ? (
            getDaysLeft() > 0 ? (
              <>
                <Typography color="error" sx={{ mb: 1 }}>
                  Your account is scheduled for deletion. It will be eligible for permanent deletion in <b>{getDaysLeft()}</b> day(s).
                </Typography>
                <Button variant="contained" color="error" disabled>
                  Delete Account (30-day countdown)
                </Button>
                {user?.deletionRequestedAt && getDaysLeft() > 0 && (
                  <Button variant="outlined" color="primary" onClick={handleCancelDeletion} disabled={cancelLoading} sx={{ mt: 2 }}>
                    {cancelLoading ? 'Cancelling...' : 'Cancel Deletion'}
                  </Button>
                )}
              </>
            ) : (
              <Button variant="contained" color="error" onClick={handlePermanentDelete} disabled={permanentDeleteLoading}>
                {permanentDeleteLoading ? 'Deleting...' : 'Delete Account Permanently'}
              </Button>
            )
          ) : (
            <Button variant="contained" color="error" onClick={handleDeleteAccount}>
              Delete Account
            </Button>
          )}
        </Box>
        {cancelSuccess && (
          <Typography color="success.main" sx={{ mt: 2 }}>
            Account deletion cancelled. Your account is active again.
          </Typography>
        )}
      </SettingsPaper>

      <SettingsPaper>
        <Typography variant="h6" gutterBottom>
          Notification Preferences
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Customize how you receive notifications from Amour Space.
        </Typography>
      </SettingsPaper>

      <SettingsPaper>
        <Typography variant="h6" gutterBottom>
          Privacy Settings
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Control your privacy and visibility settings.
        </Typography>
      </SettingsPaper>

      {deletionRequested && undoToken && (
        <SettingsPaper>
          <Typography variant="h6" color="error" gutterBottom>
            Account Deletion Scheduled
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Your account is scheduled for deletion. You have 30 days to undo this action.<br />
            <b>Undo Token:</b> <code>{undoToken}</code>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Please save this token. If you change your mind, contact support or use the undo feature (coming soon).
          </Typography>
        </SettingsPaper>
      )}
    </SettingsContainer>
  );
};

export default Settings; 